import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Area, 
  AreaChart, 
  CartesianGrid, 
  ResponsiveContainer, 
  Tooltip, 
  XAxis, 
  YAxis, 
  ReferenceLine 
} from "recharts";
import { MetalPrice, TimePeriod } from "@/lib/metal-price";
import { useWindowSize } from "@/hooks/use-window-size";

interface SilverChartProps {
  silverPriceHistory: MetalPrice[];
  selectedTimePeriod: TimePeriod;
}

export function SilverChart({ silverPriceHistory, selectedTimePeriod }: SilverChartProps) {
  const { width } = useWindowSize();
  const isSmallScreen = width < 768;

  const formatTimestamp = (timestamp: string, period: TimePeriod) => {
    // Special case for 'all' period with year-only timestamps
    if (period === 'all' && /^\d{4}$/.test(timestamp)) {
      return timestamp; // Just return the year as is
    }
    
    // For other periods or if timestamp is not just a year
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return timestamp; // If invalid date, return the original timestamp
    }
    
    switch (period) {
      case "week":
        return date.toLocaleDateString([], { weekday: 'short' });
      case "month":
        return date.toLocaleDateString([], { day: 'numeric', month: 'short' });
      case "year":
        return date.toLocaleDateString([], { month: 'short' });
      case "all":
        return date.getFullYear().toString();
      default:
        return date.toLocaleDateString();
    }
  };

  // Calculate the average price
  const silverAvgPrice = useMemo(() => {
    if (!silverPriceHistory || silverPriceHistory.length === 0) return 0;
    const sum = silverPriceHistory.reduce((acc, item) => acc + item.price, 0);
    return sum / silverPriceHistory.length;
  }, [silverPriceHistory]);

  // Determine the ideal domain for the Y axis
  const silverYAxisDomain = useMemo(() => {
    if (!silverPriceHistory || silverPriceHistory.length === 0) return [0.8, 1.1];
    
    const values = silverPriceHistory.map(d => d.price);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min;
    
    // If the range is very small, expand the domain
    const padding = range < 0.1 ? 0.1 : range * 0.1;
    
    return [min - padding, max + padding];
  }, [silverPriceHistory]);

  const sortedSilverHistory = useMemo(() => {
    return [...silverPriceHistory].sort((a, b) => {
      // Special case for 'all' period with year-only timestamps
      if (selectedTimePeriod === 'all' && /^\d{4}$/.test(a.timestamp) && /^\d{4}$/.test(b.timestamp)) {
        return parseInt(a.timestamp) - parseInt(b.timestamp);
      }
      
      // For other periods or if timestamps are not just years
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      
      // Check if dates are valid
      if (isNaN(dateA.getTime()) || isNaN(dateB.getTime())) {
        // Fallback to string comparison if dates are invalid
        return a.timestamp.localeCompare(b.timestamp);
      }
      
      return dateA.getTime() - dateB.getTime();
    });
  }, [silverPriceHistory, selectedTimePeriod]);

  const responsiveSilverHistory = useMemo(() => {
    // No downsampling on large screens.
    if (!isSmallScreen) {
      return sortedSilverHistory;
    }

    const originalLength = sortedSilverHistory.length;
    let desiredPoints;

    // Determine the desired number of points based on period and screen width
    if (selectedTimePeriod === 'all') {
      // For the 'all' period on small screens, be very aggressive to ensure readability.
      // We aim for a small number of points (e.g., 10-15), representing larger time intervals.
      const divisor = 100; // More aggressive divisor
      desiredPoints = Math.max(7, Math.min(15, Math.floor(width / divisor)));
    } else {
      // For shorter periods, we can afford more data points.
      const divisor = 50; // Standard divisor
      desiredPoints = Math.max(7, Math.floor(width / divisor));
    }

    // If we already have a reasonable number of points, no need to downsample.
    if (originalLength <= desiredPoints) {
      return sortedSilverHistory;
    }

    // Calculate interval to get down to the desired number of points.
    const interval = Math.max(1, Math.floor(originalLength / desiredPoints));
    console.log(`[SilverChart] Downsampling for '${selectedTimePeriod}' on small screen. Original: ${originalLength}, Target: ${desiredPoints}, Interval: ${interval}, Final Points: ${Math.floor(originalLength / interval)}`);
    
    return sortedSilverHistory.filter((_, i) => i % interval === 0);

  }, [sortedSilverHistory, selectedTimePeriod, isSmallScreen, width]);

  return (
    <Card className="shadow-lg border-silver/20">
      <CardHeader>
        <CardTitle className="text-silver dark:text-silver-light text-2xl">Storico Prezzi Argento</CardTitle>
      </CardHeader>
      <CardContent className="h-96">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={responsiveSilverHistory}
            margin={{ top: 25, right: 30, left: 15, bottom: 15 }}
          >
            <defs>
              <linearGradient id="silverGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--silver))" stopOpacity={0.6} />
                <stop offset="95%" stopColor="hsl(var(--silver))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false}
              opacity={0.15} 
              stroke="hsl(var(--muted-foreground))"
            />
            <XAxis
              dataKey="timestamp"
              tickFormatter={(timestamp) => formatTimestamp(timestamp, selectedTimePeriod)}
              tick={{ fontSize: 12, fill: "hsl(var(--foreground))" }}
              stroke="hsl(var(--muted-foreground))"
              tickMargin={12}
              minTickGap={25}
              padding={{ left: 10, right: 10 }}
            />
            <YAxis
              domain={silverYAxisDomain}
              tick={{ fontSize: 12, fill: "hsl(var(--silver))" }}
              stroke="hsl(var(--silver))"
              tickLine={{ stroke: "hsl(var(--silver))" }}
              axisLine={{ stroke: "hsl(var(--silver))" }}
              tickMargin={12}
              tickCount={6}
              allowDecimals={true}
              tickFormatter={(value) => value.toFixed(2)}
              label={{ 
                value: 'Prezzo (€/g)', 
                angle: -90, 
                position: 'insideLeft', 
                offset: 0,
                style: { 
                  textAnchor: 'middle', 
                  fill: 'hsl(var(--silver))',
                  fontSize: 14,
                  fontWeight: 'bold'
                } 
              }}
            />
            <Tooltip
              formatter={(value) => [`€${Number(value).toFixed(2)}`, 'Prezzo']}
              labelFormatter={(label) => {
                // Special case for 'all' period with year-only timestamps
                if (selectedTimePeriod === 'all' && /^\d{4}$/.test(label)) {
                  return `Anno ${label}`;
                }
                
                // For other periods or if label is not just a year
                const date = new Date(label);
                if (isNaN(date.getTime())) {
                  return String(label);
                }
                
                return date.toLocaleString();
              }}
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                borderColor: 'hsl(var(--border))', 
                borderRadius: '0.75rem',
                boxShadow: '0 8px 16px rgba(0,0,0,0.15)',
                padding: '12px 16px'
              }}
              itemStyle={{ 
                color: 'hsl(var(--silver))',
                fontWeight: 'bold',
                padding: '5px 0'
              }}
              cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '5 5' }}
            />
            <ReferenceLine 
              y={silverAvgPrice} 
              stroke="hsl(var(--silver))" 
              strokeDasharray="3 3" 
              opacity={0.6}
              label={{
                value: `Media: €${silverAvgPrice.toFixed(2)}`,
                position: 'insideBottomLeft',
                fill: 'hsl(var(--silver))',
                fontSize: 11
              }}
            />
            <Area
              type="monotone"
              dataKey="price"
              name="Argento"
              stroke="hsl(var(--silver))"
              strokeWidth={3}
              fillOpacity={0.6}
              fill="url(#silverGradient)"
              dot={false}
              activeDot={{ 
                r: 7, 
                strokeWidth: 2, 
                stroke: 'hsl(var(--background))',
                fill: 'hsl(var(--silver))'
              }}
              animationDuration={800}
              animationEasing="ease-in-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
